import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useFormikContext, FieldArray } from 'formik';

import {
    ISkillListItemFragment,
    ISkillRequirementFragment,
} from 'graphql/types';
import { AddButton } from 'common/components/Button/AddButton';
import { Divider } from 'common/components/Divider';
import { ListPanel } from 'common/components/ListPanel';
import { SkillSelectDrawer } from 'skill/components/SkillSelectDrawer';
import { getSkillSelectStatus } from 'skill/utils/skillSelect';

import { GroupSkillFieldListItem } from './GroupSkillFieldListItem';

const toSkillListItem = (
    req: ISkillRequirementFragment
): ISkillListItemFragment => req.skill as ISkillListItemFragment;

const toSkillRequirement = (
    skill: ISkillListItemFragment,
    existing?: ISkillRequirementFragment
): ISkillRequirementFragment => ({
    __typename: 'SkillRequirement',
    skill,
    minAbsolute: existing?.minAbsolute ?? 0,
    minPercent: existing?.minPercent ?? 0,
    hasMandatoryCertificates: existing?.hasMandatoryCertificates ?? false,
    compliancy: existing?.compliancy ?? null,
});

interface IProps {
    name: string;
    value: ISkillRequirementFragment[];
}

export const GroupSkillField = ({ name, value }: IProps) => {
    const { setFieldValue } = useFormikContext();
    const [translate] = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);

    const addedSkillItems = value.map(toSkillListItem);

    const [selectedSkills, setSelectedSkills] =
        useState<ISkillListItemFragment[]>(addedSkillItems);

    const handleSelectSkill = (skill: ISkillListItemFragment) => {
        const newSkills = [...selectedSkills, skill].sort((a, b) =>
            a.name > b.name ? 1 : -1
        );

        setSelectedSkills(newSkills);
    };

    const handleAddSkills = () => {
        const newRequirements = selectedSkills.map((skill) => {
            const existing = value.find((req) => req.skill?.id === skill.id);

            return toSkillRequirement(skill, existing);
        });

        setFieldValue(name, newRequirements);
        setDrawerOpen(false);
    };

    const handleRemoveSkill = (
        skill: ISkillListItemFragment,
        removeAdded?: boolean
    ) => {
        const { isSelected } = getSkillSelectStatus(
            selectedSkills,
            addedSkillItems,
            skill
        );

        if (isSelected) {
            const newSelectedSkills = selectedSkills.filter(
                (selectedSkill) => selectedSkill.id !== skill.id
            );

            setSelectedSkills(newSelectedSkills);
        }

        // When an added skill is removed, it needs to be removed from the form
        if (!removeAdded) return;

        const newRequirements = value.filter(
            (req) => req.skill?.id !== skill.id
        );

        setFieldValue(name, newRequirements);
    };

    return (
        <FieldArray
            name={name}
            render={(arrayHelpers) => {
                const { form } = arrayHelpers;
                const addedRequirements =
                    (form.values[name] as ISkillRequirementFragment[]) || [];
                const error = form.errors[name];
                const touched = form.touched[name];
                const topLevelError =
                    typeof error === 'string' ? error : undefined;

                const addButtonHeader = (
                    <AddButton
                        aria-label={translate('groupSkillField.addButton')}
                        error={
                            !!topLevelError && touched
                                ? topLevelError
                                : undefined
                        }
                        onClick={() => setDrawerOpen(true)}
                    >
                        {translate('groupSkillField.addButton')}
                    </AddButton>
                );

                return (
                    <>
                        <ListPanel header={addButtonHeader}>
                            {!addedRequirements.length && (
                                <Typography sx={{ py: 2 }} variant="body2">
                                    {translate('groupSkillField.noSkillsAdded')}
                                </Typography>
                            )}

                            {addedRequirements.map((req, index) => (
                                <Box key={req.skill?.id}>
                                    <GroupSkillFieldListItem
                                        fieldName={`${name}[${index}]`}
                                        skill={
                                            req.skill as ISkillListItemFragment
                                        }
                                        onDelete={(skill) => {
                                            handleRemoveSkill(skill, true);
                                        }}
                                    />

                                    {index < addedRequirements.length - 1 && (
                                        <Divider />
                                    )}
                                </Box>
                            ))}
                        </ListPanel>

                        <SkillSelectDrawer
                            addedSkills={addedRequirements.map(toSkillListItem)}
                            open={drawerOpen}
                            selectedSkills={selectedSkills}
                            setSelectedSkills={setSelectedSkills}
                            onAddSkills={handleAddSkills}
                            onClose={() => setDrawerOpen(false)}
                            onRemoveSkill={handleRemoveSkill}
                            onSelectSkill={handleSelectSkill}
                        />
                    </>
                );
            }}
        />
    );
};
